/**
 * ReportHeaderStrip — Reader Polish Prompt 3.
 *
 * Replaces the previously-stacked panels above the cover (action
 * toolbar, score+tier hero card, export buttons row, SED-version
 * banner, sanity pill, etc.) with a single ~32px-tall strip:
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  10/05/2026 · v1.0 · SED v7              Edit  Rerun  ⋮     │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The score + verdict signal lives on the cover; this strip is
 * purely metadata + actions. Rerun and Review quality details stay
 * surfaced as primary buttons; everything else collapses into the ⋮
 * More menu (state actions, PDF, DOCX, PPTX, SED, KMZ, Delete).
 *
 * NO BANNERS (2026-10-13, William: "i dont want any banners on report
 * pages"). The strip is the report page's only status surface:
 *   - ONE compact status chip (`statusChip`, resolved by the parent with
 *     `resolveReportStatusChip`) names the most important state; the former
 *     banner text is its hover title AND its aria-describedby, so keyboard
 *     and screen-reader users reach it too;
 *   - "Generating report…" (spinner + progress) replaces Rerun while a run
 *     for this site + report type is in flight;
 *   - "Review quality details" replaced the Insights button;
 *   - every action that only existed on a banner is a ⋮ entry.
 *
 * Pure presentational — the parent owns every piece of state and
 * passes handlers in. Side-effect-free so tests can mount the
 * strip without the SiteRagReportView surroundings.
 */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { MoreVertical, ClipboardCheck, ChevronLeft, Info, ChevronDown, Check, Loader2 } from 'lucide-react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { SiteEntity } from '../../../types/site';
import { computeReportDocStats } from '../../../lib/reportDocStats';
import { resolveDraftTierUx } from '../../../lib/draftTierUx';
import { openAuditFindings } from '../../../lib/audit/openAuditFlag';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { REPORT_TYPE_REGISTRY } from '../../../config/reportTypes';
import type { ReportStatusChip } from './reportStatusChip';

/**
 * EV2 — argument shape for the always-on Site Evidence breadcrumb +
 * legacy missing-docs pill click. Carries an optional `filterReportId`
 * so the canonical Site Evidence tab can pre-scope to variables cited
 * by THIS report. Parents that don't yet plumb the deep-link param can
 * still pass `() => navigate('…/evidence')` (the parens-less arg form
 * works because every property is optional).
 */
export interface OpenSiteEvidenceOpts {
  /** EV3 — when set, the canonical tab opens with `?filterReportId=…`
   *  so SiteEvidenceTab can compute the cited-variable scope on mount. */
  filterReportId?: string;
}

export interface ReportHeaderStripProps {
  report: SiteRagReport;
  /**
   * DRC-3 — live site entity. When provided, the header strip renders
   * the canonical `report-header-doc-count` chip using
   * `computeReportDocStats(report, site).includedAndLive`, the same
   * number the Reports row chip and the Documents-informing subhead
   * render. Omitting it suppresses the chip (legacy free-standing
   * reports / preview surfaces without a site context). The strip
   * never reaches into a store — the parent passes the value
   * resolved from `siteRag.sites.find(...)`.
   */
  site?: SiteEntity;
  /** Primary analysis action. Reruns current report types by default. */
  onRerun: () => void;
  /**
   * Override for successor-workflow handoffs. Legacy Decision Guide artifacts
   * use this to open Path to GO without suggesting the frozen report reruns.
   */
  rerunLabel?: string;
  /** Optional "Delete" entry in the ⋮ menu. */
  onDelete?: () => void;
  /** Optional "Export PDF" entry. */
  onExportPdf?: () => void;
  onOpenSummary?: () => void;
  onExportSummaryPdf?: () => void;
  /** A durable PDF request is already being followed by this reader. */
  pdfBuildPending?: boolean;
  /** Styled "Download as Word" export — the superior reportDocxExport
   *  path (template-aware, chart-embedding, docx-smoke-gated). The
   *  inferior counsel-docx wiring (`onExportCounselDocx` →
   *  `downloadSiteReportDocx`) was retired 2026-06 for site reports;
   *  this is now the single Word export entry point. */
  onExportDocx?: () => void;
  /** Optional PowerPoint (.pptx) export — slide deck mirroring the DOCX subset. */
  onExportPptx?: () => void;
  /** Optional "Export SED" entry. */
  onExportSed?: () => void;
  /** Optional "Export KMZ" entry. */
  onExportKmz?: () => void;
  /** "Review quality details" primary button — opens the site Audit tab's
   *  Report-quality block for this report. Replaced the "Insights" button
   *  (2026-10-13), which opened the same destination. */
  onReviewQuality?: () => void;
  /** Badge count rendered on the Review quality details button when > 0. */
  reviewQualityBadgeCount?: number;
  /**
   * The ONE status chip (2026-10-13 — no banners on report pages). Resolved
   * by the parent (`resolveReportStatusChip`) because the strip never reaches
   * into a store. `kind: 'generating'` also hides Rerun until the run ends.
   */
  statusChip?: ReportStatusChip | null;
  // ── Former banner-only actions, now ⋮ entries (2026-10-13) ──────────
  /** Truth questions → the selected report's Audit truth queue. */
  onConfirmTruth?: () => void;
  truthQuestionCount?: number;
  /** Regenerate a genuine legacy draft at investment-grade quality. */
  onFinalizeDraft?: () => void;
  isFinalizing?: boolean;
  /** Degraded narrative → narrative-only refresh. */
  onRefreshNarrative?: () => void;
  /** High-severity evidence conflicts → Site Evidence, conflicts focused. */
  onReviewConflicts?: () => void;
  /** Quarantined / withheld / flagged sections → Section quality notes dialog. */
  onOpenSectionNotes?: () => void;
  sectionNoteCount?: number;
  /** Admin — misconfigured providers → System Health. */
  onOpenProviderHealth?: () => void;
  /** Platform admin — failed PDF delivery → rebuild. */
  onRetryPdf?: () => void;
  /** RI4 — number of critical Supporting Documents rows that are
   *  still unsatisfied for this report's type (post-EA tier-aware
   *  resolution). When > 0, the EV2 breadcrumb embeds a compact
   *  "N missing" warning chip alongside the evidence-completeness %. */
  missingCriticalCount?: number;
  /**
   * Report-type checklist field coverage (Coverage Matrix logic).
   * When null/undefined, the evidence breadcrumb badge is hidden.
   */
  reportFieldCoveragePct?: number | null;
  /** EV2 — called when the user clicks the always-on Site Evidence
   *  breadcrumb. Accepts an `OpenSiteEvidenceOpts` so the parent can
   *  forward `filterReportId` to scope the canonical tab to the cited
   *  variables for this report (EV3). When omitted, the breadcrumb is
   *  not rendered — callers that don't know how to navigate shouldn't
   *  advertise a dead click target. */
  onOpenSiteEvidence?: (opts?: OpenSiteEvidenceOpts) => void;
  /** Admin-only — resolved template name driving cover/sections. */
  templateName?: string | null;
  /** Admin-only — resolved cover variant (`classic`, `hero-image`, etc.). */
  coverLayout?: string | null;
  /** Admin-only — true when report.templateId matches the resolved template. */
  templateLinked?: boolean;
  /** Admin-only — show template linkage chip (parent gates on isAdmin). */
  showTemplateLinkChip?: boolean;
  /** Admin-only — saved templates offered in the link dropdown. */
  availableTemplates?: Array<{ id: string; name: string; version?: number }>;
  /** Admin-only — the report's currently-linked templateId (null = unlinked). */
  currentTemplateId?: string | null;
  /** Admin-only — link / change the report's template to the chosen id. */
  onLinkTemplate?: (templateId: string) => void | Promise<void>;
  /** When supplied, renders a back affordance to the site's Analyses tab. */
  onBackToReports?: () => void;

}

/** Chip tones — compact, token-based, same family as the old drift chip. */
const STATUS_CHIP_TONE: Record<ReportStatusChip['tone'], string> = {
  progress: 'bg-primary/10 text-primary',
  danger: 'bg-danger/10 text-danger',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-surface-2 text-text-secondary',
};

interface MenuItem {
  label: string;
  onClick: () => void;
  testId: string;
  variant?: 'default' | 'danger';
  /** Cost P3 — when true the row is non-clickable and shows `disabledReason`. */
  disabled?: boolean;
  /** Tooltip + hint text explaining why the row is disabled. */
  disabledReason?: string;
}

/**
 * EXPORT-TIME AUDIT WARNING (2026-08-25, William) — an audit-flagged report
 * exports freely, but the moment of sharing is where the caveat matters: one
 * confirm dialog names what is open and recommends fixing + re-running first,
 * then lets the user proceed. Point-of-action friction instead of ambient
 * banners; ADVISORY, never a bar (AUDIT-ADVISORY, v1.27.0).
 *
 * Exported because the Reports-gallery Export button (SiteLandingPage) opens
 * `/print/{id}` in a new tab — a second export path that must carry the SAME
 * warning off the SAME predicate, or the friction is only one menu deep.
 */
export function ExportAuditWarningDialog({
  report,
  onCancel,
  onProceed,
}: {
  report: SiteRagReport;
  onCancel: () => void;
  onProceed: () => void;
}) {
  // Number AND noun come from the one predicate. `kind` exists precisely so
  // this sentence can be true without a second, disagreeing count: reviewer
  // issues are not untrue statements, and a flag with nothing itemizable
  // standing cannot name any number of them.
  const audit = openAuditFindings(report);
  const one = audit.count === 1;
  const heading = audit.kind === 'truth-disagreement'
    ? `${audit.count} statement${one ? '' : 's'} in this analysis disagree${one ? 's' : ''} with the site evidence`
    : audit.kind === 'audit-finding'
      ? `${audit.count} audit finding${one ? ' is' : 's are'} open on this analysis`
      : 'This analysis is flagged for audit';
  const remedy = audit.kind === 'truth-disagreement'
    ? "confirm what's true"
    : audit.kind === 'audit-finding'
      ? 'resolve the findings'
      : 'review the audit';
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle id="export-audit-warning-title">{heading}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-text-secondary">
          Best practice: {remedy} and re-run the report before sharing it —
          the exported file will carry an &quot;Audit recommended&quot; disclosure either way.
          {report.qualityHold === 'needs-revision' && (
            <> Exporting for private review does not clear the authoritative quality hold or approve this analysis for decision use.</>
          )}
        </p>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel} data-testid="export-audit-warning-cancel">
            Cancel
          </Button>
          <Button type="button" onClick={onProceed} data-testid="export-audit-warning-proceed">
            Export anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReportHeaderStrip(props: ReportHeaderStripProps) {
  const { report, onRerun, statusChip } = props;
  const generating = statusChip?.kind === 'generating';
  const statusDetailId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the menu on outside-click / Escape. Single effect, mounted
  // only when the menu is open so we don't churn document listeners
  // on every report-view render.
  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    // Now that the strip is sticky (position:sticky top-0), the kebab
    // dropdown is an inline absolute child that travels with the bar.
    // Closing it on scroll keeps the surface tidy while the user moves
    // through a long report. A wheel/trackpad gesture over the absolutely
    // positioned menu may not produce a scroll event on the report scroller,
    // so listen for `wheel` as well. Passive + capture observes the gesture
    // without consuming it; the report remains free to scroll.
    function onScroll() {
      setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('wheel', onScroll, { passive: true, capture: true });
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('wheel', onScroll, { capture: true } as EventListenerOptions);
      window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
    };
  }, [menuOpen]);

  // Template-link dropdown (the NOT-LINKED chip). Mirrors the kebab menu's
  // outside-click / Escape dismissal; no scroll-close so the user can scroll
  // a long template list inside the popover.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const linkRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!linkOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!linkRef.current) return;
      if (!linkRef.current.contains(e.target as Node)) setLinkOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setLinkOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [linkOpen]);

  // Build the menu in priority order. Each entry is gated on the
  // matching handler being provided, so absent flows simply drop
  // out of the list rather than rendering disabled rows.
  const items: MenuItem[] = [];
  // SET-D1 (2026-05-17) — the "Documents" menu entry (and its
  // associated `onOpenDocuments` prop) was removed. The top-level
  // Documents tab (7-tab IA in SiteLandingPage) is the canonical
  // doc-library surface, so duplicating it in the report's
  // ⋮ menu was redundant. Sentinel test guards the deletion.
  // EV1 (2026-05-15) — the "Evidence" entry that opened an inline
  // SEDBrowserPanel above the cover was removed. The canonical surface
  // is the Site Evidence tab, reached via the always-on breadcrumb
  // below (EV2). Per-claim citation markers (CT6) replace the
  // "browse evidence while reading" workflow with something focused.
  // Deliverable exports fail closed, but the recovery instruction must match
  // the actual report state. `isDraft` is also set by quality holds, so generic
  // "Finalize this draft" copy misdirected held, degraded, metadata-only and
  // invalid reports. The shared resolver reserves Finalize for explicit legacy
  // draft-tier artifacts and gives every other state truthful next steps.
  const deliveryUx = resolveDraftTierUx(report);
  const audit = openAuditFindings(report);
  const [pendingExport, setPendingExport] = useState<(() => void) | null>(null);
  const exportBarred = deliveryUx.exportBarred;
  const exportBlockedHint = deliveryUx.exportHint;
  /*
   * Hold the export behind ExportAuditWarningDialog (above) whenever the
   * report carries an open audit. The pending runner IS the export, so
   * "Export anyway" runs the very handler the menu row would have.
   */
  const guardExport = (run: () => void): (() => void) =>
    audit.flag ? () => setPendingExport(() => run) : run;
  // State actions first (2026-10-13) — each used to live only on a report-page
  // banner. Gated on the parent's handler, exactly like the export rows, so
  // an entry appears only while its state holds.
  if (props.onConfirmTruth) {
    const n = props.truthQuestionCount ?? 0;
    items.push({ label: n > 0 ? `Confirm what's true (${n})` : "Confirm what's true", onClick: props.onConfirmTruth, testId: 'header-menu-confirm-truth' });
  }
  if (props.onFinalizeDraft) items.push({ label: props.isFinalizing ? 'Finalizing…' : 'Finalize legacy draft', onClick: props.onFinalizeDraft, testId: 'header-menu-finalize-draft', disabled: Boolean(props.isFinalizing) });
  if (props.onRefreshNarrative) items.push({ label: 'Refresh narrative', onClick: props.onRefreshNarrative, testId: 'header-menu-refresh-narrative' });
  if (props.onReviewConflicts) items.push({ label: 'Review evidence conflicts', onClick: props.onReviewConflicts, testId: 'header-menu-review-conflicts' });
  if (props.onOpenSectionNotes) {
    const n = props.sectionNoteCount ?? 0;
    items.push({ label: n > 0 ? `Section quality notes (${n})` : 'Section quality notes', onClick: props.onOpenSectionNotes, testId: 'header-menu-section-notes' });
  }
  if (props.onOpenProviderHealth) items.push({ label: 'Open System Health', onClick: props.onOpenProviderHealth, testId: 'header-menu-provider-health' });
  if (props.onRetryPdf) items.push({ label: props.pdfBuildPending ? 'Retrying PDF…' : 'Retry PDF build', onClick: props.onRetryPdf, testId: 'header-menu-retry-pdf', disabled: props.pdfBuildPending, disabledReason: props.pdfBuildPending ? 'A PDF request is already being prepared.' : undefined });
  if (props.onOpenSummary) items.push({ label: report.reportType && report.reportType !== 'feasibility' ? 'Three-page summary' : 'Screening summary', onClick: props.onOpenSummary, testId: 'header-menu-summary' });
  if (props.onExportSummaryPdf) items.push({ label: 'Download summary PDF', onClick: guardExport(props.onExportSummaryPdf), testId: 'header-menu-summary-pdf', disabled: exportBarred || props.pdfBuildPending, disabledReason: exportBlockedHint });
  if (props.onExportPdf)     items.push({ label: props.pdfBuildPending ? 'Preparing PDF…' : 'Download PDF', onClick: guardExport(props.onExportPdf), testId: 'header-menu-export-pdf', disabled: exportBarred || props.pdfBuildPending, disabledReason: props.pdfBuildPending ? 'Your PDF request is already being prepared.' : exportBlockedHint });
  // The two former Word entries ("Download as Word" → onExportCounselDocx
  // / docxBuilder, and "Export DOCX" → onExportDocx / reportDocxExport)
  // collapsed into a single superior path 2026-06. We keep the
  // reportDocxExport handler (template-aware, chart-embedding,
  // docx-smoke-gated) under the user-facing "Download as Word" label.
  if (props.onExportDocx)    items.push({ label: 'Download as Word', onClick: guardExport(props.onExportDocx), testId: 'header-menu-export-docx', disabled: exportBarred, disabledReason: exportBlockedHint });
  if (props.onExportPptx)    items.push({ label: 'Export PowerPoint', onClick: guardExport(props.onExportPptx), testId: 'header-menu-export-pptx', disabled: exportBarred, disabledReason: exportBlockedHint });
  if (props.onExportSed)     items.push({ label: 'Export SED',  onClick: props.onExportSed,  testId: 'header-menu-export-sed' });
  if (props.onExportKmz)     items.push({ label: 'Export KMZ',  onClick: props.onExportKmz,  testId: 'header-menu-export-kmz' });
  if (props.onDelete)        items.push({ label: 'Delete report', onClick: props.onDelete, testId: 'header-menu-delete', variant: 'danger' });

  // Date + version + SED ref — small mono inline metadata. Empty
  // pieces drop out so the strip stays clean for reports that haven't
  // run through every pipeline yet. (The "N% evidence" chip that used
  // to sit here was removed — SED completeness is shown in the report
  // body's quality section; the chip was redundant and noisy.)
  const versionLabel = `v${report.version || 1}.${report.minorVersion || 0}`;
  const sedLabel = report.sedVersionUsed != null ? `SED v${report.sedVersionUsed}` : null;
  const dateLabel = new Date(report.createdAt ?? Date.now()).toLocaleDateString();
  // Aurora Cockpit breadcrumb sub-line — the human report-type label.
  // Pure config lookup (REPORT_TYPE_REGISTRY is static), so the strip stays
  // side-effect-free. Falls back to the raw key for any unknown type.
  const reportTypeLabel =
    (report.reportType ? REPORT_TYPE_REGISTRY[report.reportType]?.shortLabel : undefined)
    ?? report.reportType
    ?? 'Report';

  // DRC-3 — canonical header doc-count. Computed once per render via
  // `computeReportDocStats` so the number agrees with the Reports row
  // chip (`reports-row-doc-count-<id>`) and the Documents-informing
  // subhead (`report-doc-count-informing`). The walkthrough symptom of
  // a "mystery 69 DOCS" header chip turned out to be the lineage chip
  // in `SiteLandingPage` whose `uppercase tracking-widest` CSS rendered
  // the lowercase "docs" literal visually as "DOCS" — that surface
  // already routes through the canonical helper (DRC-1). The actual
  // report header band had NO doc chip until now, which broke the
  // cross-surface invariant the test pack pins. Adding it here closes
  // the loop. We always include the `sr-only` testid span — even when
  // the visible chip is suppressed by `count === 0` or by `site` being
  // undefined — so the cross-surface parity test can locate the
  // testid by name without playing keep-away with the conditional
  // render. Number.isFinite() guard on the count just-in-case a
  // pathological report mutates the helper output before we read it.
  const docCount = useMemo(() => {
    if (!props.site) return null;
    const { includedAndLive } = computeReportDocStats(report, props.site);
    return Number.isFinite(includedAndLive) ? includedAndLive : null;
  }, [report, props.site]);
  const showDocCountChip = docCount != null && docCount > 0;

  const exportWarningDialog = pendingExport !== null && (
    <ExportAuditWarningDialog
      report={report}
      onCancel={() => setPendingExport(null)}
      onProceed={() => {
        const run = pendingExport;
        setPendingExport(null);
        run?.();
      }}
    />
  );

  return (
    <>
    {exportWarningDialog}
    <div
      // The strip's scroll container is SiteRagEngine's <main> (overflow-y-auto),
      // which already begins directly under the 56px app top-bar (a non-sticky flex
      // child OUTSIDE the scroll area). `position: sticky` resolves against that
      // <main>, so `top-0` pins the strip flush to the top of the scroll viewport.
      // The old `top-[90px]` left a ~90px white band below the app bar on scroll — it
      // assumed a window-level scroll under the app bar PLUS a SiteRagReportShell
      // header, but neither applies in the live viewer (the shell is not mounted here;
      // see SiteRagReportView.tsx:225,3194). z-30 keeps it above the scrolling body.
      className="sticky top-0 z-30 flex items-center gap-3 px-3.5 py-2.5 rounded-xl border border-border bg-surface-1/90 shadow-card backdrop-blur-md
                 bg-[linear-gradient(180deg,color-mix(in_srgb,var(--primary)_4%,transparent),transparent_60%)]"
      role="toolbar"
      aria-label="Report console"
      data-testid="report-header-strip"
    >
      {/* ── LEFT · breadcrumb (‹ Reports / {siteName} + report-type sub) ── */}
      <nav className="flex items-center gap-2 min-w-0 shrink" aria-label="Breadcrumb">
        {props.onBackToReports && (
          <>
            <button
              type="button"
              onClick={props.onBackToReports}
              className="inline-flex items-center gap-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-text-muted hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded px-0.5 py-1"
              data-testid="header-back-to-reports"
              // The stable destination is `/sites/{id}?tab=reports`, now the
              // promoted Analyses workspace. The test id deliberately keeps
              // the legacy handler name so external test selectors survive.
              aria-label="Back to Analyses"
            >
              <ChevronLeft className="w-3 h-3" aria-hidden="true" />
              Analyses
            </button>
            <span className="text-border-strong font-mono" aria-hidden>/</span>
          </>
        )}
        <span className="min-w-0">
          <span
            className="block font-display text-[1.0625rem] leading-[1.15] font-semibold text-text-primary truncate max-w-[22rem]"
            title={`${report.siteName} – ${reportTypeLabel}`}
          >
            {report.siteName}
          </span>
          <span className="block font-mono text-[10px] uppercase tracking-[0.12em] text-text-muted mt-px">
            {reportTypeLabel}
            <span className="mx-1">·</span>
            <span data-testid="header-date">{dateLabel}</span>
          </span>
        </span>
      </nav>

      {/* DRC-3 — canonical header doc-count. Kept OUTSIDE the responsive
          center cluster (which is display:none below md) so this sr-only
          cross-surface invariant span is always in the DOM and AT-readable
          regardless of viewport. Renders whenever `site` is wired. */}
      {docCount != null && (
        <span className="sr-only" data-testid="report-header-doc-count">
          {docCount}
        </span>
      )}

      {/* ── CENTER · status cluster — glow-dot stat chips ── */}
      <div
        className="hidden md:flex items-stretch ml-1 border-l border-r border-border-subtle shrink-0"
        role="group"
        aria-label="Report status"
      >
        <span className="flex flex-col gap-0.5 px-3.5 justify-center">
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-text-muted">
            <i className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_var(--dot-glow)_color-mix(in_srgb,var(--primary)_var(--glow-amount),transparent)]" aria-hidden />
            Version
          </span>
          <span className="font-mono text-[13px] font-semibold tabular-nums text-text-primary" data-testid="header-version">
            {versionLabel}
          </span>
        </span>
        {sedLabel && (
          <span className="flex flex-col gap-0.5 px-3.5 justify-center border-l border-border-subtle">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-muted">SED</span>
            <span className="font-mono text-[13px] font-semibold tabular-nums text-text-muted" data-testid="header-sed">
              {sedLabel}
            </span>
          </span>
        )}
        {/* DRC-3 — visible doc-count stat (the sr-only invariant span lives
             above, outside this responsive cluster). Gated on count > 0. */}
        {showDocCountChip && (
          <span className="flex flex-col gap-0.5 px-3.5 justify-center border-l border-border-subtle">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-muted">Docs</span>
            <span
              className="font-mono text-[13px] font-semibold tabular-nums text-text-muted"
              data-testid="report-header-doc-count-chip"
              title={`${docCount} document${docCount === 1 ? '' : 's'} informing this report`}
              aria-label={`${docCount} documents informing this report`}
            >
              {docCount} doc{docCount === 1 ? '' : 's'}
            </span>
          </span>
        )}
      </div>

      {/* meta + actions group share the trailing flex space; ml-auto pins the
          cluster to the right edge of the console. */}
      <div className="flex items-center gap-2 flex-wrap min-w-0 ml-auto">
        {/* The ONE status chip (2026-10-13) — replaces every report-page
            banner and the old "Refresh required" chip. Compact, inside the
            toolbar; the former banner text is the hover title and, for
            keyboard / screen-reader users, the focusable chip's description. */}
        {statusChip && (
          <>
            <span
              tabIndex={0}
              className={`${generating ? 'inline-flex' : 'sr-only'} items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary ${STATUS_CHIP_TONE[statusChip.tone]}`}
              title={statusChip.detail}
              aria-describedby={statusDetailId}
              data-testid="report-header-status"
              data-kind={statusChip.kind}
            >
              {generating && (
                <Loader2
                  className="w-3 h-3 animate-spin shrink-0"
                  aria-hidden="true"
                  data-testid="report-header-status-spinner"
                />
              )}
              <span>{statusChip.label}</span>
              {generating && statusChip.progressPercent != null && (
                <span className="font-mono tabular-nums">{statusChip.progressPercent}%</span>
              )}
            </span>
            <span id={statusDetailId} className="sr-only" data-testid="report-header-status-detail">
              {statusChip.detail}
            </span>
          </>
        )}
        {/* Admin lineage chip — DEMOTED from the old amber TEMPLATE/COVER/
            NOT-LINKED alarm to a compact neutral "Lineage" meta chip. The
            full template + cover + linkage detail lives in the title tooltip;
            the chip only tints (warning) when the report is genuinely NOT
            linked to its resolved template. testid + semantics preserved. */}
        {props.showTemplateLinkChip && props.templateName && (
          <div className="relative" ref={linkRef}>
            <button
              type="button"
              onClick={() => setLinkOpen(o => !o)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-mono text-[10.5px] uppercase tracking-[0.06em] border transition-colors ${
                props.templateLinked
                  ? 'border-border bg-surface-2 text-text-muted hover:bg-surface-3'
                  : 'border-warning/45 text-warning hover:bg-warning/10'
              }`}
              data-testid="report-template-link-chip"
              aria-haspopup="listbox"
              aria-expanded={linkOpen}
              title={
                props.templateLinked
                  ? `Template: ${props.templateName}${props.coverLayout ? ` · Cover: ${props.coverLayout}` : ''}. This report is LINKED – its cover, section order and colours resolve from this saved template. Click to change the linked template.`
                  : `This report is NOT LINKED to a saved template, so it can't pick up a template's cover layout, section order or colours – it renders with stock defaults. Click to link it to a template.`
              }
            >
              <Info className="w-3 h-3 shrink-0" aria-hidden="true" />
              <span className="truncate max-w-[140px] normal-case tracking-normal">
                {props.templateLinked ? props.templateName : 'Template: not linked'}
              </span>
              <ChevronDown className="w-3 h-3 shrink-0 opacity-70" aria-hidden="true" />
            </button>
            {linkOpen && (
              <div
                className="absolute right-0 top-full mt-1 z-50 w-72 rounded-md border border-border bg-surface-1 shadow-xl py-1"
                role="listbox"
                data-testid="report-template-link-menu"
              >
                <div className="px-3 py-2 border-b border-border-subtle">
                  <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-text-muted">
                    {props.templateLinked ? 'Change linked template' : 'Link a template'}
                  </div>
                  <p className="mt-1 text-[11px] leading-snug text-text-secondary">
                    {props.templateLinked
                      ? 'This report renders from the saved template below – pick another to re-link its cover, sections and colours (no content is re-generated).'
                      : "This report isn't linked to any saved template, so it uses stock defaults. Pick one to link its cover, sections and colours (no content is re-generated)."}
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto py-1">
                  {(props.availableTemplates ?? []).length === 0 ? (
                    <div className="px-3 py-2 text-[11px] text-text-muted">
                      No saved templates yet – create one in Template Studio.
                    </div>
                  ) : (
                    (props.availableTemplates ?? []).map(t => {
                      const isCurrent = !!props.currentTemplateId && t.id === props.currentTemplateId;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          role="option"
                          aria-selected={isCurrent}
                          disabled={isCurrent || linkBusy}
                          onClick={async () => {
                            if (isCurrent || !props.onLinkTemplate) { setLinkOpen(false); return; }
                            setLinkBusy(true);
                            try {
                              await props.onLinkTemplate(t.id);
                              setLinkOpen(false);
                            } finally {
                              setLinkBusy(false);
                            }
                          }}
                          className={`w-full text-left px-3 py-1.5 text-[12px] flex items-center gap-2 transition-colors ${
                            isCurrent ? 'text-text-muted cursor-default' : 'text-text-primary hover:bg-surface-2'
                          } ${linkBusy ? 'opacity-60' : ''}`}
                          data-testid={`report-template-link-option-${t.id}`}
                        >
                          <span className="flex-1 truncate">{t.name || 'Untitled template'}</span>
                          {isCurrent && (
                            <span className="inline-flex items-center gap-1 shrink-0 text-[10px] uppercase tracking-wide text-success">
                              <Check className="w-3.5 h-3.5" aria-hidden="true" /> Linked
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* ── RIGHT · action cluster — Rerun · Review quality details · ⋮ as
          one cohesive group. Rerun steps aside while a new version is
          generating (the status chip says "Generating report…"). ── */}
      <div className="flex items-center gap-1.5 shrink-0">
        {!generating && REPORT_TYPE_REGISTRY[report.reportType ?? 'feasibility'].status !== 'retired' && <button
          type="button"
          onClick={onRerun}
          className="da-btn h-7"
          data-testid="header-rerun"
        >
          {props.rerunLabel ?? 'Rerun'}
        </button>}
        {props.onReviewQuality && (
          <button
            type="button"
            onClick={props.onReviewQuality}
            className="da-btn h-7"
            data-testid="header-review-quality"
            aria-label={props.reviewQualityBadgeCount ? `Review quality details (${props.reviewQualityBadgeCount} need attention)` : 'Review quality details'}
          >
            <ClipboardCheck className="w-3 h-3" aria-hidden="true" />
            Review quality details
            {props.reviewQualityBadgeCount && props.reviewQualityBadgeCount > 0 ? (
              <span className="ml-0.5 type-section font-bold px-1 py-0.5 rounded bg-warning/15 text-warning border border-warning/30">
                {props.reviewQualityBadgeCount}
              </span>
            ) : null}
          </button>
        )}
        {items.length > 0 && (
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              className="da-btn da-btn--icon h-7 w-7 justify-center"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="More report actions"
              data-testid="header-more-toggle"
            >
              <MoreVertical className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-1 z-30 min-w-[160px] rounded-md overflow-hidden bg-card border border-border shadow-lg"
                data-testid="header-more-menu"
              >
                {items.map(item => (
                  <button
                    key={item.testId}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    aria-disabled={item.disabled || undefined}
                    title={item.disabled ? item.disabledReason : undefined}
                    onClick={() => {
                      if (item.disabled) return;
                      setMenuOpen(false);
                      item.onClick();
                    }}
                    className={`w-full text-left px-3 py-2 type-body focus-visible:outline-none ${
                      item.disabled
                        ? 'cursor-not-allowed opacity-50 text-text-muted'
                        : `hover:bg-state-hover focus-visible:bg-state-hover ${item.variant === 'danger' ? 'text-danger hover:text-danger' : 'text-text-primary'}`
                    }`}
                    data-testid={item.testId}
                  >
                    {item.label}
                    {item.disabled && item.disabledReason && (
                      <span className="block text-[10px] leading-snug text-text-secondary mt-0.5 normal-case">
                        {item.disabledReason}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
